import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Divider,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { postSOWDetails } from "../../http/requests/sow";
import { getSOWData } from "../../redux/actions";
import { getAllClients, getListItems } from "../../http/requests/dropdown";
import { getEmployeeDropdownData } from "../../http/requests/session";
import { toastOptions } from "../../common/utils/toasterOptions";
import "./SOWAddModal.css";

const SOWAddModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const sowListClientFilterId = useSelector((s) => s.MODULES.sowListClientFilterId);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [sowTypeOptions, setSowTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    sowName: "",
    clientId: "",
    sowStartDate: null,
    sowEndDate: null,
    sowType: "",
    resourceCount: "",
    namedSOWFlag: false,
    status: "",
    deliveryManagerUserId: "",
    accountManagerUserID: "",
    note: "",
  });

  useEffect(() => {
    if (!open) return;
    const loadDropdowns = async () => {
      try {
        const [clientsRes, typeRes, statusRes, employeesRes] = await Promise.all([
          getAllClients(),
          getListItems("projectType"),
          getListItems("projectStatus"),
          getEmployeeDropdownData(),
        ]);
        setClients(Array.isArray(clientsRes) ? clientsRes : []);
        setSowTypeOptions(Array.isArray(typeRes) ? typeRes : []);
        setStatusOptions(Array.isArray(statusRes) ? statusRes : []);
        setEmployees(Array.isArray(employeesRes) ? employeesRes : []);
      } catch (err) {
        console.error("Failed to load dropdowns:", err);
        toast.error("Failed to load form options", toastOptions);
      }
    };
    loadDropdowns();
  }, [open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    setFormData({
      sowName: "",
      clientId: "",
      sowStartDate: null,
      sowEndDate: null,
      sowType: "",
      namedSOWFlag: false,
      status: "",
      deliveryManagerUserId: "",
      accountManagerUserID: "",
      note: "",
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sowName?.trim()) {
      toast.error("SOW Name is required", toastOptions);
      return;
    }
    const rawClientId = formData.clientId;
    const clientIdNum = typeof rawClientId === "number" ? rawClientId : parseInt(rawClientId, 10);
    if (rawClientId === "" || rawClientId == null || Number.isNaN(clientIdNum) || clientIdNum <= 0) {
      toast.error("Client is required", toastOptions);
      return;
    }
    if (!formData.sowStartDate) {
      toast.error("Start Date is required", toastOptions);
      return;
    }
    if (!formData.sowEndDate) {
      toast.error("End Date is required", toastOptions);
      return;
    }
    if (!formData.sowType) {
      toast.error("SOW Type is required", toastOptions);
      return;
    }
    const start = dayjs(formData.sowStartDate);
    const end = dayjs(formData.sowEndDate);
    if (end.isBefore(start)) {
      toast.error("End Date must be on or after Start Date", toastOptions);
      return;
    }

    try {
      setSubmitting(true);
      const resourceCountVal = formData.resourceCount !== "" && formData.resourceCount != null && !Number.isNaN(Number(formData.resourceCount)) ? Number(formData.resourceCount) : null;
      const payload = {
        sowName: formData.sowName.trim(),
        clientId: clientIdNum,
        sowStartDate: start.format("YYYY-MM-DD"),
        sowEndDate: end.format("YYYY-MM-DD"),
        sowType: formData.sowType,
        resourceCount: resourceCountVal,
        namedSOWFlag: formData.namedSOWFlag,
        status: formData.status?.trim() || null,
        deliveryManagerUserId: formData.deliveryManagerUserId?.trim() || null,
        accountManagerUserID: formData.accountManagerUserID?.trim() || null,
        note: formData.note?.trim() || null,
        active: true,
      };
      await postSOWDetails(payload);
      toast.success("SOW created successfully", toastOptions);
      dispatch(
        getSOWData({
          pageNo: 1,
          pageSize: 10,
          sortBy: "sowName",
          sortDir: "ASC",
          searchData: "",
          clientId: sowListClientFilterId,
        })
      );
      handleClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage || err?.data?.errorMessage || err?.message || "Failed to save SOW",
        toastOptions
      );
    } finally {
      setSubmitting(false);
    }
  };

  const clientId = (c) => c?.id ?? c?.Id;
  const clientName = (c) => c?.name ?? c?.Name ?? "";
  const itemId = (o) => o?.id ?? o?.Id;
  const shortCode = (o) => o?.shortCodeValue ?? o?.ShortCodeValue ?? "";
  const longCode = (o) => o?.longCodeValue ?? o?.LongCodeValue ?? "";
  const empAzureId = (e) => e?.azureId ?? e?.AzureId ?? "";
  const empName = (e) => e?.name ?? e?.Name ?? "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          width: "55%",
          minWidth: 320,
        },
      }}
    >
      <DialogTitle sx={{ color: "#1773bc", fontWeight: 600, pb: 0 }}>
        Create SOW
      </DialogTitle>
      <Divider sx={{ borderColor: "#1773bc", opacity: 0.5 }} />
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Client</InputLabel>
              <Select
                value={formData.clientId}
                label="Client"
                onChange={(e) => handleChange("clientId", e.target.value)}
              >
                <MenuItem value="">
                  <em>Select Client</em>
                </MenuItem>
                {clients.map((c) => (
                  <MenuItem key={clientId(c)} value={clientId(c)}>
                    {clientName(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="SOW Name"
              value={formData.sowName}
              onChange={(e) => handleChange("sowName", e.target.value)}
              required
              size="small"
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
                <DatePicker
                  label="Start Date"
                  value={formData.sowStartDate}
                  onChange={(value) => handleChange("sowStartDate", value)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" required />
                  )}
                />
                <DatePicker
                  label="End Date"
                  value={formData.sowEndDate}
                  onChange={(value) => handleChange("sowEndDate", value)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" required />
                  )}
                />
              </Box>
            </LocalizationProvider>
            <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
              <FormControl fullWidth size="small" required>
                <InputLabel>SOW Type</InputLabel>
                <Select
                  value={formData.sowType}
                  label="SOW Type"
                  onChange={(e) => handleChange("sowType", e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select SOW Type</em>
                  </MenuItem>
                  {sowTypeOptions.map((opt) => (
                    <MenuItem key={itemId(opt)} value={shortCode(opt)}>
                      {shortCode(opt)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select Status</em>
                  </MenuItem>
                  {statusOptions.map((opt) => (
                    <MenuItem key={itemId(opt)} value={longCode(opt)}>
                      {longCode(opt)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
              <Autocomplete
                size="small"
                fullWidth
                options={employees}
                getOptionLabel={(opt) => empName(opt) || ""}
                isOptionEqualToValue={(opt, val) => empAzureId(opt) === empAzureId(val)}
                value={
                  employees.find(
                    (e) => empAzureId(e) === formData.deliveryManagerUserId
                  ) ?? null
                }
                onChange={(_, val) =>
                  handleChange("deliveryManagerUserId", val ? empAzureId(val) : "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Delivery Manager" placeholder="Search..." />
                )}
              />
              <Autocomplete
                size="small"
                fullWidth
                options={employees}
                getOptionLabel={(opt) => empName(opt) || ""}
                isOptionEqualToValue={(opt, val) => empAzureId(opt) === empAzureId(val)}
                value={
                  employees.find(
                    (e) => empAzureId(e) === formData.accountManagerUserID
                  ) ?? null
                }
                onChange={(_, val) =>
                  handleChange("accountManagerUserID", val ? empAzureId(val) : "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Account Manager" placeholder="Search..." />
                )}
              />
            </Box>
            <TextField
              fullWidth
              label="Resource Count"
              type="number"
              value={formData.resourceCount}
              onChange={(e) => handleChange("resourceCount", e.target.value)}
              size="small"
              inputProps={{ step: "0.01", min: "0" }}
              placeholder="e.g. 5.5"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.namedSOWFlag}
                  onChange={(e) => handleChange("namedSOWFlag", e.target.checked)}
                  color="primary"
                />
              }
              label="Named SOW"
            />
            <TextField
              fullWidth
              label="Note"
              value={formData.note}
              onChange={(e) => handleChange("note", e.target.value)}
              multiline
              rows={2}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: "#1773bc" }}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SOWAddModal;
